import React from 'react';
import { LogOut } from 'lucide-react';
import { motion } from 'framer-motion';
import { NotificationCenter } from './NotificationCenter';
import { Link } from 'react-router-dom';
import { cn } from '../../utils/cn';
import { BrandMark } from '../ui/BrandMark';
import { MediaImage } from '../ui/MediaImage';
import { AppVersionLabel } from '../ui/AppVersionLabel';
import { BRAND_FOOTER } from '../../config/branding';

const roleLabels = {
  'super-admin': 'Super Admin',
  'content-creator': 'Content Creator',
  student: 'Student',
  parent: 'Parent',
};
export function Layout({ children, view, user, onLogout }) {
  const normalizedName = (user?.name || '').trim().toLowerCase();
  const normalizedRoleLabel = (roleLabels[user?.role] || '').trim().toLowerCase();
  const showRoleBadge = !!user && normalizedName && normalizedName !== normalizedRoleLabel;
  const usesSidebarShell = view === 'super-admin' || view === 'content-creator';
  const shellWidth = 'max-w-[min(100rem,calc(100%-1.5rem))]';
  const shellAlign = usesSidebarShell ? 'mr-auto ml-0 w-full max-w-none' : 'mx-auto ' + shellWidth;
  const shellPadding = usesSidebarShell
    ? 'px-3 py-4 sm:px-4 sm:py-6 sm:pl-4 sm:pr-6 md:py-8 md:pl-6 md:pr-10 lg:pl-8 lg:pr-12'
    : 'px-3 py-4 sm:px-4 sm:py-6 md:px-6 md:py-8';

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col w-full overflow-x-hidden selection:bg-indigo-100 selection:text-indigo-700">
      <header className="sticky top-0 z-50 border-b border-slate-200/70 bg-white/85 shadow-sm shadow-slate-900/[0.04] backdrop-blur-md supports-[backdrop-filter]:bg-white/75">
        <div
          className={cn(
            'flex h-14 items-center justify-between gap-2 sm:h-16 md:h-20',
            usesSidebarShell
              ? 'mr-auto ml-0 w-full max-w-none px-3 sm:px-4 sm:pl-4 md:pl-6 md:pr-10 lg:pl-8'
              : cn('mx-auto', shellWidth, 'px-3 sm:px-4 md:px-6')
          )}
        >
          <motion.div
            initial={{
              opacity: 0,
              x: -20,
            }}
            animate={{
              opacity: 1,
              x: 0,
            }}
            className="flex min-w-0 items-center gap-2 sm:gap-3"
          >
            <div className="sm:hidden">
              <BrandMark compact />
            </div>
            <div className="hidden sm:block">
              <BrandMark />
            </div>
          </motion.div>
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <AppVersionLabel variant="badge" short className="shrink-0" />
            {user && (
              <React.Fragment>
                <NotificationCenter />
                <Link
                  to="/profile"
                  className="hidden sm:flex items-center gap-2 text-sm max-w-[120px] md:max-w-none truncate px-2 py-2 rounded-xl hover:bg-slate-50 transition-all"
                  title="Profile"
                >
                  {user.avatarUrl ? (
                    <MediaImage
                      src={user.avatarUrl}
                      alt={user.name}
                      className="w-8 h-8 rounded-xl object-cover border border-slate-200"
                    />
                  ) : null}
                  <span className="text-slate-600 font-medium truncate">{user.name}</span>
                  {showRoleBadge && (
                    <span className="hidden md:inline px-2 py-1 bg-indigo-100 text-indigo-700 rounded-lg text-xs font-bold shrink-0">
                      {roleLabels[user.role]}
                    </span>
                  )}
                </Link>
              </React.Fragment>
            )}
            <button
              onClick={onLogout}
              type="button"
              className="flex min-h-[44px] min-w-[44px] items-center justify-center gap-1 rounded-xl px-3 py-2.5 text-sm font-bold text-slate-600 transition-all duration-200 hover:bg-rose-50 hover:text-rose-600 sm:min-w-0 sm:gap-2 sm:px-4 sm:py-2"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>
      <main className={cn('w-full flex-1', shellAlign, shellPadding)}>
        <motion.div
          key={view}
          initial={{
            opacity: 0,
            y: 20,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.4,
            ease: 'easeOut',
          }}
          className="min-w-0"
        >
          {children}
        </motion.div>
      </main>
      <footer className="border-t border-slate-200/60 bg-white/90 py-5 backdrop-blur-sm sm:py-6 md:py-8">
        <div
          className={cn(
            'px-3 text-center sm:px-4',
            usesSidebarShell
              ? 'mr-auto ml-0 w-full md:pl-6 md:pr-10 lg:pl-8'
              : cn('mx-auto', shellWidth, 'md:px-6')
          )}
        >
          <p className="text-xs sm:text-sm font-medium text-slate-400">{BRAND_FOOTER}</p>
          <AppVersionLabel align="center" className="mt-1.5" />
        </div>
      </footer>
    </div>
  );
}
